import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Answer } from '@/components/question/forms/shared/Answer';
import { OrderingItemCreateDTO } from '@/models/Answer';
import { Divider } from '@/components/ui/daisy';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type OrderingItemTextProps = {
  item: OrderingItemCreateDTO;
  totalItems: number;
  isEditing: boolean;
  onStartEdit: () => void;
  onFinishEdit: () => void;
  className?: string;
  onUpdateText: (text: string) => void;
  onUpdateValidPositions: (validPositions: number[]) => void;
  onSwitchToMedia: () => void;
  onMarkIncorrect: () => void;
  onDelete: () => void;
  isLoading: boolean;
};

export default function OrderingItemText({
  item,
  totalItems,
  isEditing,
  onStartEdit,
  onFinishEdit,
  className,
  onUpdateText,
  onUpdateValidPositions,
  onSwitchToMedia,
  onMarkIncorrect,
  onDelete,
  isLoading,
}: OrderingItemTextProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t: tOrdering } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.orderingForm',
  });

  const isCorrect = item.validPositions.length > 0;
  return (
    <Answer isCorrect={isCorrect} isEditing={isEditing} className={className}>
      <ConditionalRenderer
        condition={isEditing}
        fallback={<Answer.TextPreview text={item.text} />}
      >
        <Answer.TextInput
          initialValue={item.text ?? ''}
          placeholder={t('newOption')}
          onConfirm={value => {
            onUpdateText(value);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
      <Answer.MoreOptions>
        <Answer.MoreOptions.Positions
          totalPositions={totalItems}
          selectedPositions={item.validPositions}
          onChange={onUpdateValidPositions}
          header={t('correctIn')}
          getPositionLabel={position =>
            tOrdering('positionLabel', {
              number: String(position).padStart(2, '0'),
            })
          }
        />
        <Divider className="my-0 py-4" />
        <Answer.MoreOptions.EditText
          onClick={onStartEdit}
          disabled={isLoading}
        />
        <Answer.MoreOptions.EditMedia
          onClick={onSwitchToMedia}
          disabled={isLimitedEdition}
        />
        <ConditionalRenderer condition={item.validPositions.length > 0}>
          <Answer.MoreOptions.MarkIncorrect
            onClick={onMarkIncorrect}
            disabled={isLimitedEdition || isLoading}
          />
        </ConditionalRenderer>
        <Answer.MoreOptions.Delete
          onClick={onDelete}
          disabled={isLimitedEdition || isLoading}
        />
      </Answer.MoreOptions>
    </Answer>
  );
}
